<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-lg-8 col-md-10 mx-auto">
        <div class="card mt-4">
          <div class="card-header p-3">
            <h5 class="mb-0">{{ isEdit ? "編輯區塊" : "新增區塊" }}</h5>
          </div>
          <div class="card-body p-3">
            <div class="row">
              <div class="col-md-6">
                <label>區塊代碼 (Key)</label>
                <div class="input-group mb-3">
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.section_key"
                    :disabled="isEdit"
                    placeholder="e.g. hero_slider"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <label>排序 (Sort Order)</label>
                <div class="input-group mb-3">
                  <input
                    type="number"
                    class="form-control"
                    v-model="form.sort_order"
                  />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>狀態</label>
              <div class="form-check form-switch ps-0">
                <input
                  class="form-check-input ms-auto"
                  type="checkbox"
                  v-model="form.is_active"
                  checked
                />
                <label
                  class="form-check-label text-body ms-3 text-truncate w-80 mb-0"
                  >啟用</label
                >
              </div>
            </div>

            <hr class="horizontal dark my-3" />
            <h6>內容設定 (Content Data)</h6>

            <!-- Hero Section Editor -->
            <div v-if="isHeroSection">
              <HeroSectionEditor v-model="form.data" />
            </div>

            <!-- Brand Story Editor -->
            <div v-if="isBrandStory">
              <BrandStoryEditor v-model="form.data" />
            </div>

            <!-- Featured Collection Editor -->
            <div v-if="isFeaturedCollection">
              <FeaturedCollectionEditor v-model="form.data" />
            </div>

            <hr class="horizontal dark my-3" />
            <button
              class="btn btn-sm btn-outline-secondary"
              type="button"
              @click="showJson = !showJson"
            >
              {{ showJson ? "隱藏進階設定 (JSON)" : "顯示進階設定 (JSON)" }}
            </button>

            <!-- Default JSON Editor (Shown if toggled or no specific editor) -->
            <div v-if="shouldShowJson || showJson" class="form-group mt-3">
              <div
                class="alert alert-info text-white text-sm"
                v-if="shouldShowJson && !showJson"
              >
                目前此類型的區塊使用 JSON 編輯器管理。
              </div>
              <label>JSON Data Source</label>
              <textarea
                class="form-control font-monospace"
                rows="10"
                v-model="jsonString"
              ></textarea>
              <small class="text-danger" v-if="jsonError">{{
                jsonError
              }}</small>
            </div>

            <div class="d-flex justify-content-end mt-4">
              <button
                type="button"
                class="btn btn-light m-0"
                @click="$router.push('/homepage-sections')"
              >
                取消
              </button>
              <button
                type="button"
                class="btn bg-gradient-primary m-0 ms-2"
                @click="save"
                :disabled="!!jsonError"
              >
                儲存
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import HeroSectionEditor from "./components/HeroSectionEditor.vue";
import BrandStoryEditor from "./components/BrandStoryEditor.vue";
import FeaturedCollectionEditor from "./components/FeaturedCollectionEditor.vue";

export default {
  name: "HomepageSectionForm",
  components: {
    HeroSectionEditor,
    BrandStoryEditor,
    FeaturedCollectionEditor,
  },
  data() {
    return {
      form: {
        section_key: "",
        sort_order: 0,
        is_active: true,
        data: {},
      },
      jsonString: "{}",
      jsonError: null,
      showJson: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    isHeroSection() {
      return this.form.section_key === "hero_slider";
    },
    isBrandStory() {
      return this.form.section_key === "brand_story";
    },
    isFeaturedCollection() {
      return (
        this.form.section_key === "featured_collection" ||
        this.form.section_key === "featured_products"
      );
    },
    shouldShowJson() {
      return (
        !this.isHeroSection && !this.isBrandStory && !this.isFeaturedCollection
      );
    },
  },
  watch: {
    jsonString(val) {
      // When JSON string changes (manual edit), update form.data object
      // BUT ignore if it matches current form.data to avoid loops if we were syncing back
      try {
        const parsed = JSON.parse(val);
        // Deep compare could be expensive, just simple check
        this.jsonError = null;
        this.form.data = parsed;
      } catch (e) {
        this.jsonError = "Invalid JSON format";
      }
    },
    "form.data": {
      handler(val) {
        // When form.data changes (from UI editor), update jsonString
        // Only update if it's valid to avoid overwriting user manual input with partials?
        // Actually, for UI editor syncing -> JSON string, always sync
        this.jsonString = JSON.stringify(val, null, 4);
      },
      deep: true,
    },
  },
  async created() {
    if (this.isEdit) {
      await this.fetchSection();
    }
  },
  methods: {
    async fetchSection() {
      try {
        const res = await request.get(
          `/api/admin/homepage-sections/${this.$route.params.id}`
        );
        this.form = res.data.data;
        this.jsonString = JSON.stringify(this.form.data, null, 4);
      } catch (e) {
        alert("載入失敗");
        this.$router.push("/homepage-sections");
      }
    },
    async save() {
      try {
        const payload = { ...this.form }; // form.data is already updated via watch or v-model
        if (this.isEdit) {
          await request.put(
            `/api/admin/homepage-sections/${this.$route.params.id}`,
            payload
          );
        } else {
          await request.post("/api/admin/homepage-sections", payload);
        }
        this.$router.push("/homepage-sections");
      } catch (e) {
        console.error(e);
        alert("儲存失敗");
      }
    },
  },
};
</script>
